/**
 * Every hard limit in the ZeliSight ingest and read path, as a named constant.
 *
 * Source of truth: build plan sections 5.9.3 (ingest limits), 5.9.5 (collector
 * processing order), 5.7 (property bounds), 6.4 (SDK queue, retry and unload),
 * 5.10 (read API) and 11.4 (abuse controls).
 *
 * The governing split, from 5.9.3: request level violations reject the request,
 * event level violations degrade the event. Rejecting a 1,000 event batch
 * because event 743 carries 101 properties is how a customer's data silently
 * disappears on retry.
 *
 * Every constant here is mirrored into services/collector/schema/contract.py by
 * codegen/to_python.ts, so the collector and the SDK can never disagree about a
 * number.
 */
/** One kibibyte. */
export const KIB = 1024;
/** One mebibyte. */
export const MIB = 1024 * 1024;
/* ------------------------------------------------------------------------- *
 * Request level. A violation rejects the whole request.
 * ------------------------------------------------------------------------- */
/** Content-Length ceiling, checked before the body is read. Over this: 413. */
export const MAX_BODY_BYTES = 1 * MIB;
/** Inflate ceiling for `Content-Encoding: gzip`, the zip bomb guard. Over this: 413. */
export const MAX_INFLATED_BODY_BYTES = 8 * MIB;
/** A batch must carry at least one event. */
export const MIN_EVENTS_PER_BATCH = 1;
/** A batch may carry at most this many events. Over this: 413 `events_per_batch`. */
export const MAX_EVENTS_PER_BATCH = 1000;
/** Longest accepted SDK name in `sdk.n`. */
export const MAX_SDK_NAME_CHARS = 64;
/** Longest accepted SDK version in `sdk.v`. */
export const MAX_SDK_VERSION_CHARS = 32;
/* ------------------------------------------------------------------------- *
 * Event level. A violation degrades the event, the batch still returns 202.
 * ------------------------------------------------------------------------- */
/** Serialised size of a single event. Over this the event is dropped and the
 *  `zs_oversize_event` metric is emitted. The batch continues. */
export const MAX_EVENT_BYTES = 64 * KIB;
/** Shortest accepted event name. */
export const MIN_EVENT_NAME_CHARS = 1;
/** Longest accepted event name. */
export const MAX_EVENT_NAME_CHARS = 200;
/** Longest accepted `distinct_id`. It becomes part of a DynamoDB sort key. */
export const MAX_DISTINCT_ID_CHARS = 200;
/** Longest accepted `anon_id`. */
export const MAX_ANON_ID_CHARS = 200;
/** Properties per event. Excess keys are dropped in insertion order. */
export const MAX_PROPERTY_COUNT = 100;
/**
 * Longest accepted property key.
 *
 * Not stated as a number in 5.9.3, but bounded here because a property key is
 * concatenated into the `PROP` sort key `<enc event_name>#<enc prop_key>#<enc
 * prop_value>` (5.2.1) and DynamoDB caps a sort key at 1,024 bytes. An
 * unbounded key would make a rollup write fail at run time rather than degrade
 * at ingest time. Matched to the event name bound for symmetry.
 */
export const MAX_PROPERTY_KEY_CHARS = 200;
/** Property string values longer than this are truncated, never dropped. */
export const MAX_STRING_VALUE_CHARS = 1024;
/**
 * Container nesting allowed inside `props`.
 *
 * Depth is measured as container depth: a scalar is 0, and a container is one
 * plus the deepest of its children. The `props` object itself counts, so
 * `{a: 1}` is depth 1, `{a: {b: {c: 1}}}` is depth 3 and is the deepest form
 * accepted. Anything below that boundary is stringified, not dropped.
 */
export const MAX_PROPERTY_DEPTH = 3;
/**
 * Lowest codepoint permitted in an event name, a property key or a
 * `distinct_id`. Everything below 0x20 is rejected so that the internal
 * sentinel namespace (rule 2 of section 5.0, NUL prefixed) stays unreachable
 * from customer supplied input.
 */
export const MIN_ALLOWED_CODEPOINT = 0x20;
/* ------------------------------------------------------------------------- *
 * Timestamps. FR-ING-10: clamp, never reject.
 * ------------------------------------------------------------------------- */
/** An event `ts` older than this relative to receive time is clamped. */
export const TS_MAX_PAST_MS = 7 * 24 * 60 * 60 * 1000;
/** An event `ts` further ahead than this relative to receive time is clamped. */
export const TS_MAX_FUTURE_MS = 60 * 60 * 1000;
/* ------------------------------------------------------------------------- *
 * SDK queue, batching, retry and unload. Section 6.4.
 * ------------------------------------------------------------------------- */
/** Default flush cadence in milliseconds. */
export const DEFAULT_FLUSH_INTERVAL_MS = 3000;
/** Default flush trigger by queued event count. */
export const DEFAULT_FLUSH_AT_EVENTS = 20;
/** Queue cap by event count. On overflow the oldest are dropped. */
export const MAX_QUEUE_EVENTS = 500;
/** Queue cap by serialised bytes. On overflow the oldest are dropped. */
export const MAX_QUEUE_BYTES = 500 * KIB;
/** Event name emitted when the queue overflows, carrying `{dropped_count}`. */
export const QUEUE_OVERFLOW_EVENT_NAME = '$queue_overflow';
/* ------------------------------------------------------------------------- *
 * Heatmaps, ADR 0034. Opt in per project through GET /v1/config.
 * ------------------------------------------------------------------------- */
/** Every heatmap event name starts with this. Rollup keeps these out of product
 *  aggregates and out of billable volume, and meters them as heatmap events. */
export const HEATMAP_EVENT_PREFIX = '$heatmap_';
/** One click: element selector, position inside the element, page position. */
export const HEATMAP_CLICK_EVENT_NAME = '$heatmap_click';
/** One page view's scroll reach and attention per twentieth of the document. */
export const HEATMAP_SCROLL_EVENT_NAME = '$heatmap_scroll';
/** Serialised ceiling for one ordinary flush request, below MAX_BODY_BYTES so a
 *  deep offline queue can never produce the classic flush time 413. */
export const MAX_REQUEST_BYTES = 900 * KIB;
/** Event ceiling for one ordinary flush request. */
export const MAX_REQUEST_EVENTS = MAX_EVENTS_PER_BATCH;
/** Payloads above this are gzipped. Below it they are sent as text/plain so the
 *  request stays a CORS simple request with no preflight. */
export const COMPRESS_ABOVE_BYTES = 2 * KIB;
/**
 * Maximum size of a single unload beacon.
 *
 * Chrome enforces a 64 KiB quota across all in flight keepalive fetches per
 * origin and `navigator.sendBeacon` shares that quota, so the queue is chunked
 * to this size and the chunks are fired sequentially against
 * BEACON_AGGREGATE_BUDGET_BYTES.
 */
export const BEACON_CHUNK_BYTES = 48 * KIB;
/** Aggregate keepalive budget for one unload. Whatever does not fit is
 *  persisted to localStorage for next load replay. */
export const BEACON_AGGREGATE_BUDGET_BYTES = 64 * KIB;
/** Full jitter backoff base: `random(0, min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** (retry - 1)))`. */
export const BACKOFF_BASE_MS = 1000;
/** Full jitter backoff ceiling. */
export const BACKOFF_MAX_MS = 30000;
/** Session idle timeout. A new session is minted past this gap. */
export const SESSION_IDLE_MS = 30 * 60 * 1000;
/** Session absolute lifetime. A new session is minted past this age. */
export const SESSION_ABSOLUTE_MS = 24 * 60 * 60 * 1000;
/** Runtime config refetch cadence, so a long lived SPA tab stays reachable. */
export const CONFIG_REFETCH_MS = 5 * 60 * 1000;
/** Response header that stops capture immediately on the next flush. */
export const KILL_SWITCH_HEADER = 'ZS-Kill';
/** Characters of the write key used to scope SDK storage keys, so two ZeliSight
 *  projects on one page never collide. */
export const STORAGE_SCOPE_CHARS = 8;
/* ------------------------------------------------------------------------- *
 * Collector and pipeline. Sections 5.9.5 and 11.4.
 * ------------------------------------------------------------------------- */
/** Ceiling for one SQS message. */
export const MAX_SQS_MESSAGE_BYTES = 200 * KIB;
/** Entries per SendMessageBatch call. */
export const SQS_SEND_BATCH_SIZE = 10;
/** `Retry-After` seconds returned with an explicit `quota_exceeded` body. */
export const QUOTA_RETRY_AFTER_SECONDS = 3600;
/** Default per project token bucket, events per minute. Degrades, never rejects. */
export const DEFAULT_RATE_LIMIT_EPM = 2000;
/** Above this rate from one `distinct_id`, events are still counted and stored
 *  but person property updates are skipped. */
export const PERSON_UPDATE_DEGRADE_EPM = 5000;
/** Meter fraction above which a free tier org is refused with `quota_exceeded`. */
export const QUOTA_GRACE_MULTIPLIER = 1.2;
/** Write key cache TTL while `quota_state` is `ok`. */
export const KEY_CACHE_TTL_SECONDS = 60;
/** Write key cache TTL once `quota_state` is anything but `ok`. */
export const KEY_CACHE_TTL_DEGRADED_SECONDS = 15;
/** Negative cache window for an unknown or revoked key. */
export const NEGATIVE_KEY_CACHE_SECONDS = 60;
/** Token bucket read cache window. */
export const RATE_BUCKET_CACHE_SECONDS = 10;
/** Exact `(project_id, event_id)` dedup window in the cold tier. */
export const DEDUP_WINDOW_DAYS = 7;
/** Ceiling on hot tier dedup work, as a fraction of the project's observed rate. */
export const HOT_DEDUP_RATE_CAP_FRACTION = 0.05;
/** Truncation applied to `first_event_json` on the project item. */
export const FIRST_EVENT_JSON_MAX_BYTES = 8 * KIB;
/** The live tap fires while a project is below this lifetime event count. */
export const LIVE_TAP_EVENT_COUNT_THRESHOLD = 100;
/** Rows returned by the live feed. */
export const LIVE_FEED_LIMIT = 100;
/** TTL on live feed rows. */
export const LIVE_FEED_TTL_SECONDS = 60 * 60;
/** CORS preflight cache. Chrome caps Access-Control-Max-Age at this value. */
export const CORS_MAX_AGE_SECONDS = 7200;
/** CloudFront cache seconds for `GET /v1/config/<key_prefix>`. */
export const CONFIG_CACHE_SECONDS = 300;
/* ------------------------------------------------------------------------- *
 * Property cardinality bounds. Section 5.7.
 * ------------------------------------------------------------------------- */
/** Only string values at or under this length become `PROP` breakdown rows. */
export const MAX_BREAKDOWN_VALUE_CHARS = 128;
/** Top values retained per (event_name, prop_key) per day. */
export const MAX_BREAKDOWN_VALUES_PER_KEY_PER_DAY = 50;
/** Distinct values in a day above which a property key is suppressed. */
export const PROPERTY_SUPPRESSION_DISTINCT_THRESHOLD = 500;
/** Distinct event names above which further new names roll into the overflow
 *  sentinel. */
export const MAX_EVENT_NAMES_PER_PROJECT = 500;
/** Distinct event names above which the dashboard shows a cardinality warning. */
export const EVENT_NAME_WARNING_THRESHOLD = 400;
/* ------------------------------------------------------------------------- *
 * Replay. Sections 5.9.6 and FR-ING-14.
 * ------------------------------------------------------------------------- */
/** Ceiling for one replay chunk posted to `/v1/r`. */
export const REPLAY_CHUNK_MAX_BYTES = 200 * KIB;
/**
 * Presigned POST policies issued per `/v1/r/presign` call.
 *
 * Every policy reserves a sequence number and writes a chunk index row, and a
 * row is written whether or not the browser ever uploads to it. Whatever is
 * left over when a session ends is an index row with no object behind it, and
 * playback signs a URL for each one: S3 answers those 403 rather than 404,
 * because the signer has no `s3:ListBucket` and S3 will not confirm that a key
 * is absent to someone who cannot list. The recording plays correctly, the
 * player skips them, and the reader sees a wall of red in the network tab.
 *
 * Measured on a real session: 30 rows reserved for 14 uploads, so more than
 * half of every manifest was URLs that could not resolve.
 *
 * Four rather than ten. A chunk is cut about every ten seconds, so this is one
 * small request roughly every forty seconds, against at most three wasted rows
 * per session instead of up to nine. Going to one would waste nothing and put a
 * round trip in front of every chunk, which is the thing batching exists to
 * avoid.
 *
 * This bounds the waste rather than removing it. The only way to write an index
 * that is true by construction is to write the row when the object lands, which
 * means an S3 event notification, because the browser uploads directly and the
 * collector never hears about it.
 */
export const PRESIGN_BATCH_SIZE = 4;
/** Lifetime of an issued presigned POST policy. */
export const PRESIGN_EXPIRY_SECONDS = 900;
/* ------------------------------------------------------------------------- *
 * Read API. Section 5.10.
 * ------------------------------------------------------------------------- */
/** Properties accepted by the event drilldown `breakdown` parameter. */
export const MAX_BREAKDOWN_PROPERTIES = 5;
/** Longest range, in days, for which `exact_uniques: true` is honoured. */
export const EXACT_UNIQUES_MAX_RANGE_DAYS = 7;
/* -- Funnels ------------------------------------------------------------- *
 *
 * Mirrored in services/api/schema/read_limits.py, which the funnel request
 * model validates against, and asserted equal by tests/unit/test_contract_limits_mirror.py.
 * A client that lets someone build a seven step funnel the server refuses is a
 * client that wastes the whole interaction before the first request.
 */
/** Fewest steps in a funnel. One step is not a funnel, it is a count. */
export const MIN_FUNNEL_STEPS = 2;
/**
 * Most steps in a funnel.
 *
 * Six because the SQL is six hand written CTE levels with unused levels bound
 * to NULL (services/query/core/sql/funnel_base.sql), so this is a property of
 * the statement rather than a product opinion. Raising it means writing SQL.
 */
export const MAX_FUNNEL_STEPS = 6;
/** Shortest conversion window: one hour. */
export const MIN_FUNNEL_WINDOW_SECONDS = 3600;
/** Longest conversion window: thirty days. */
export const MAX_FUNNEL_WINDOW_SECONDS = 30 * 24 * 3600;
/** Conversion window when the caller does not choose: seven days. */
export const DEFAULT_FUNNEL_WINDOW_SECONDS = 7 * 24 * 3600;
/** Cap on the people returned for one drop off step. */
export const MAX_FUNNEL_DROPOFF_ROWS = 1000;
/** Query cache lifetime for a batched read. */
export const QUERY_CACHE_TTL_SECONDS = 900;
/** Row cap on an async export. */
export const MAX_EXPORT_ROWS = 1000000;
/** Standard error of the `p=12` HyperLogLog used for unique counts. */
export const HLL_ERROR_PCT = 1.6;
/** Method string reported alongside any approximate unique count. */
export const HLL_METHOD = 'hyperloglog_p12';
//# sourceMappingURL=limits.js.map